<script setup>
import { ref, computed, onMounted } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'
import { useRateCard } from '@/composables/rate-card'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import GroupEdit from '@/components/shared/GroupEdit.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import CollapsibleDetailHeader from '@/components/shared/CollapsibleDetailHeader.vue'
import DescriptionSection from '@/components/shared/DescriptionSection.vue'
import FileViewer from '@/components/shared/FileViewer.vue'
import GroupsViewDetail from '@/components/planner/GroupsViewDetail.vue'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const { t } = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { getServiceStructureTypeChip, getServiceTagChips } = useChips()
const rateCard = useRateCard()
const editItem = ref(null)

const group = ref(props.modelValue || null)
let changed = false

onMounted(async() => {
  const fetches = []
  if (planningStore.equipments.length === 0) fetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  if (planningStore.staff.length === 0) fetches.push(planningStore.fetchStaffsAsync({ all: true }, true))
  if (planningStore.groups.length === 0) fetches.push(planningStore.fetchGroupsAsync({ all: true }, true))
  await Promise.all(fetches)
  if (group.value?.id) group.value = (await planningStore.fetchGroupByIdAsync(group.value.id)).data
})

const tagChips = computed(() => getServiceTagChips(group.value?.tags))
const chipSize = computed(() => webSiteStore.groupDetailHeaderCompact ? 'sm' : 'md')

const unitSymbol = computed(() => rateCard.unitSymbol(group.value))
const vatLabel = computed(() => rateCard.vatLabel(group.value))
const priceLabel = computed(() => rateCard.priceLabel(group.value))
const costLabel = computed(() => rateCard.costLabel(group.value))
const hasRateCard = computed(() => rateCard.hasRateCard(group.value))

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => { await planningStore.archiveGroupAsync(id, { skipErrorMessage: true }) },
  onDelete: async(id, config) => { await planningStore.deleteGroupAsync(id, config) },
})

const onArchive = async(item) => {
  await archiveAsync(item.id, item.archived)
  item.archived = !item.archived
  changed = true
}

const onNavigateBack = () => {
  changed ? emit('update') : emit('cancel')
}

const emit = defineEmits(['cancel', 'update'])
useNavigator(onNavigateBack)

const reloadGroupAsync = async() => {
  group.value = (await planningStore.fetchGroupByIdAsync(group.value.id)).data
  changed = true
}

const onEditItemUpdate = async() => {
  await reloadGroupAsync()
  editItem.value = null
}

useEntityChanges([entityChangeTypeEnum.GROUP], (change) => change.id === group.value?.id && reloadGroupAsync(), () => editItem.value !== null)
</script>
<template>
  <q-card class="column full-height" :style="`padding-bottom: ${isMobile ? '4rem' : 'initial'}`">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      persistent
      class="dialog-sm"
    >
      <group-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onEditItemUpdate"
        @cancel="onEditItemUpdate"
        @delete="editItem = null; emit('update')"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <navigate-back
      :text="group?.name"
      :archived="group?.archived"
      @cancel="onNavigateBack"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-show="!group?.archived && webSiteStore.can(webSiteStore.permissionFlagEnum.MASTER_DATA)"
            flat
            round
            icon="mdi-pencil"
            @click="editItem = { ...group }"
          />
          <q-btn
            v-show="webSiteStore.can(webSiteStore.permissionFlagEnum.DELETE)"
            flat
            round
            :icon="group?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
            @click="onArchive(group)"
          />
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none" style="min-height: 0;">
      <collapsible-detail-header
        v-model:compact="webSiteStore.groupDetailHeaderCompact"
        :avatar="group?.avatar_file || group?.avatar_url"
        avatar-icon="mdi-select-group"
      >
        <template #chips>
          <chip-template :values="getServiceStructureTypeChip(group?.structure_type)" :size="chipSize" />
          <chip-template v-if="tagChips.length" :values="tagChips" :size="chipSize" />
        </template>
        <template #info>
          <q-list class="q-px-sm">
            <q-item v-if="group?.code">
              <q-item-section>
                <q-item-label caption>{{ t('common.code') }}</q-item-label>
                <q-item-label>{{ group.code }}</q-item-label>
              </q-item-section>
            </q-item>
            <description-section v-if="group?.description" :text="group.description" />
          </q-list>
        </template>
      </collapsible-detail-header>

      <q-list v-if="hasRateCard" class="q-px-sm">
        <q-item v-if="priceLabel || costLabel">
          <div v-if="priceLabel" style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ t('common.price') }}</q-item-label>
              <q-item-label>{{ unitSymbol ? `${priceLabel} / ${unitSymbol}` : priceLabel }}</q-item-label>
            </q-item-section>
          </div>
          <div v-if="costLabel" style="width: 50%; float: right;">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ t('common.cost') }}</q-item-label>
              <q-item-label style="text-align: right;">{{ unitSymbol ? `${costLabel} / ${unitSymbol}` : costLabel }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <q-item v-if="unitSymbol || vatLabel">
          <div v-if="unitSymbol" style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ t('common.unitOfMeasure') }}</q-item-label>
              <q-item-label>{{ unitSymbol }}</q-item-label>
            </q-item-section>
          </div>
          <div v-if="vatLabel" style="width: 50%; float: right;">
            <q-item-section style="display: flex; align-content: flex-end;">
              <q-item-label caption style="text-align: right;">{{ t('common.vat') }}</q-item-label>
              <q-item-label style="text-align: right;">{{ vatLabel }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
      </q-list>

      <q-separator class="q-my-sm" />
      <groups-view-detail v-if="group" :group="group" />

      <template v-if="group?.service_files?.length">
        <q-separator class="q-my-sm" />
        <q-card-section class="q-pt-none">
          <q-item-label header class="q-pa-none q-mb-sm">
            {{ t('common.attachment', group.service_files.length) }}
          </q-item-label>
          <file-viewer
            v-for="file in group.service_files"
            :key="file.id"
            :mobile="isMobile"
            hide-delete
            :cell-info="{ value: [file] }"
            @download="async(e) => await planningStore.downloadServiceFileAsync(e.id, e.name)"
          />
        </q-card-section>
      </template>
    </q-card-section>
  </q-card>
</template>
